import { MouseEventHandler, useEffect, useReducer } from 'react';
import { DOUBLE_CLICK_DELAY } from '../../../lib/constants';
import { THookState } from './types';

const getReducer = <T extends HTMLDivElement>() => (
  state: THookState<T>,
  action: { type: 'update'; payload: THookState<T> },
): THookState<T> => {
  switch (action.type) {
    case 'update':
      return { ...action.payload };
    default:
      throw new Error();
  }
};

const getInitialState = <T extends HTMLDivElement>(): THookState<T> => ({
  count: 0,
  event: null,
});

export const useSingleAndDoubleClick = <T extends HTMLDivElement>(
  singleClickHandler: MouseEventHandler<T>,
  doubleClickHandler: MouseEventHandler<T>,
  delay = DOUBLE_CLICK_DELAY,
) => {
  const reducer = getReducer<T>();
  const initialState = getInitialState<T>();
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const timer = setTimeout(() => {
      // Simple click
      if (state.count === 1 && state.event !== null) {
        singleClickHandler(state.event);
      }
      dispatch({
        type: 'update',
        payload: { count: 0, event: null },
      });
    }, delay);

    // The duration between this click and the previous one
    // is less than the value of delay = double-click
    if (state.count === 2 && state.event !== null) {
      doubleClickHandler(state.event);
    }

    return () => clearTimeout(timer);
  }, [state.count]);

  return (event: THookState<T>['event']) =>
    dispatch({
      type: 'update',
      payload: { count: state.count + 1, event },
    });
};
